import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ExternalLink, Building2 } from "lucide-react";

/**
 * OutsourceBanner — appears on QuoteDetailPage when one or more operations
 * cannot be performed in the user's workshop (e.g. a prismatic block is
 * routed to outsource because the user only owns lathes). Encourages the
 * user to publish a marketplace RFQ for the missing operations, pre-filling
 * the form with this quote's context via `?prefill={quote_id}`.
 *
 * Visibility rule: render only when `outsource_count >= 1`.
 */
export default function OutsourceBanner({ quote }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rows = quote?.calculation?.breakdown;

  const { outsourceRows, outsourceCount, totalOps, allOutsourced } = useMemo(() => {
    const breakdown = rows || [];
    const outs = breakdown.filter((r) => r.outsource === true || r.owned === false);
    return {
      outsourceRows: outs,
      outsourceCount: outs.length,
      totalOps: breakdown.length,
      allOutsourced: breakdown.length > 0 && outs.length === breakdown.length,
    };
  }, [rows]);

  if (outsourceCount === 0) return null;

  const externalCats = Array.from(
    new Set(outsourceRows.map((r) => r.external_machine_name || r.machine_name).filter(Boolean))
  ).slice(0, 3);

  const goToMarketplace = () => {
    navigate(`/marketplace?prefill=${quote.id}&action=create`);
  };

  const titleKey = allOutsourced
    ? t("outsource.titleAll")
    : t("outsource.titlePartial", { n: outsourceCount, total: totalOps });
  const bodyKey = t("outsource.body");
  const ctaKey = t("outsource.cta");

  return (
    <div
      className="mb-6 border border-amber-500/50 bg-gradient-to-r from-amber-500/[0.08] to-amber-500/[0.03] rounded-sm p-4"
      data-testid="outsource-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-sm bg-amber-500/15 border border-amber-500/40 flex items-center justify-center shrink-0">
          <AlertTriangle size={16} className="text-amber-300"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-amber-300 mb-1">
            // OUTSOURCE_REQUIRED · {outsourceCount}/{totalOps}
          </div>
          <div className="font-display font-semibold text-slate-100 text-base mb-1">
            {titleKey}
          </div>
          <p className="text-sm text-slate-300 leading-relaxed">{bodyKey}</p>

          {externalCats.length > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                {t("outsource.suggested")}:
              </span>
              {externalCats.map((cat, i) => (
                <span
                  key={`ext-${i}`}
                  className="inline-flex items-center gap-1.5 text-[11px] font-mono-data text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-0.5"
                  data-testid={`outsource-cat-${i}`}
                >
                  <Building2 size={10}/> {cat.replace(/^Outsource:\s*/i, "")}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={goToMarketplace}
          className="shrink-0 px-4 py-2.5 rounded-sm bg-amber-500 hover:bg-amber-400 text-slate-950 font-mono text-xs uppercase tracking-wider flex items-center gap-2 transition-colors"
          data-testid="outsource-publish-rfq-btn"
        >
          {ctaKey} <ExternalLink size={13}/>
        </button>
      </div>
    </div>
  );
}
